import { WHITE, CORNERS, EDGES, DANGEROUS } from './gameConstants.js'
import { getValidMoves, getFlips, calculateScore } from './gameRules.js'

/**
 * AI策略配置
 */
const AI_CONFIG = {
  CORNER_BONUS: 1000,      // 角落权重
  EDGE_BONUS: 50,          // 边缘权重
  DANGEROUS_PENALTY: -500, // 危险位置惩罚
  MOBILITY_BONUS: 10,      // 移动性奖励
  POSITION_WEIGHT: 1,      // 位置权重
  FLIP_WEIGHT: 1           // 翻转数量权重
}

/**
 * 计算位置的基本分数
 * @param {number} index - 位置索引
 * @returns {number} 位置分数
 */
function getPositionScore(index) {
  let score = 0

  if (CORNERS.includes(index)) {
    score += AI_CONFIG.CORNER_BONUS
  } else if (EDGES.includes(index)) {
    score += AI_CONFIG.EDGE_BONUS
  } else if (DANGEROUS.includes(index)) {
    score += AI_CONFIG.DANGEROUS_PENALTY
  }

  return score
}

/**
 * 简单贪心AI策略
 * 优先选择能翻转最多棋子的位置，考虑位置权重
 * @param {number[]} board - 棋盘数组
 * @returns {number} AI选择的落子位置索引
 */
export function getGreedyMove(board) {
  const moves = getValidMoves(board, WHITE)

  if (moves.length === 0) {
    return -1
  }

  let bestMove = moves[0]
  let bestScore = -Infinity

  for (const move of moves) {
    const flips = getFlips(board, move, WHITE).length
    const positionScore = getPositionScore(move)
    const totalScore = flips * AI_CONFIG.FLIP_WEIGHT +
                      positionScore * AI_CONFIG.POSITION_WEIGHT

    if (totalScore > bestScore) {
      bestScore = totalScore
      bestMove = move
    }
  }

  return bestMove
}

/**
 * 改进的AI策略，考虑移动性和位置策略
 * @param {number[]} board - 棋盘数组
 * @returns {number} AI选择的落子位置索引
 */
export function getSmartMove(board) {
  const moves = getValidMoves(board, WHITE)

  if (moves.length === 0) {
    return -1
  }

  let bestMove = moves[0]
  let bestScore = -Infinity

  for (const move of moves) {
    const flips = getFlips(board, move, WHITE).length
    const positionScore = getPositionScore(move)

    // 模拟落子后的棋盘状态
    const simulatedBoard = [...board]
    simulatedBoard[move] = WHITE
    getFlips(board, move, WHITE).forEach(flip => {
      simulatedBoard[flip] = WHITE
    })

    // 计算对手的移动性（对手可选步数越少越好）
    const opponentMoves = getValidMoves(simulatedBoard, 1) // 假设对手是黑棋
    const mobilityBonus = (64 - opponentMoves.length) * AI_CONFIG.MOBILITY_BONUS

    const totalScore = flips * AI_CONFIG.FLIP_WEIGHT +
                      positionScore * AI_CONFIG.POSITION_WEIGHT +
                      mobilityBonus

    if (totalScore > bestScore) {
      bestScore = totalScore
      bestMove = move
    }
  }

  return bestMove
}

/**
 * 根据难度选择AI策略
 * @param {number[]} board - 棋盘数组
 * @param {string} difficulty - 难度级别 'easy' | 'medium' | 'hard'
 * @returns {number} AI选择的落子位置索引
 */
export function getAIMove(board, difficulty = 'medium') {
  switch (difficulty) {
    case 'easy':
      return getGreedyMove(board)
    case 'hard':
      return getSmartMove(board)
    case 'medium':
    default:
      // 中等难度随机选择简单策略和改进策略
      return Math.random() > 0.5 ? getGreedyMove(board) : getSmartMove(board)
  }
}